// External
import React, { useState } from 'react';
import ReactMapGL, { Marker } from 'react-map-gl';
import * as _ from 'lodash';
import { gql, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { number } from 'yup/lib/locale';
import { ExclamationCircleIcon } from '@heroicons/react/solid';
import InputMask from 'react-input-mask';
import { useHistory } from 'react-router-dom';
import { pickBy, isArray } from 'lodash';

// Internal
import config from '../../../../config';
import MarkerIcon from '../../../../assets/icons/CL-Icon-Transparent-32px.png';
import {
  ListingDetails_ListingData_listing,
  ListingDetails_ListingData_listing_property,
} from '../../../../types/graph/ListingDetails_ListingData';
import { SaveFormButton, CancelFormButton } from '../../../../components';
import useNotifier from '../../../../hooks/useNotifier';

// Local Types
type Viewport = {
  width: number | string;
  height: number | string;
  latitude: number;
  longitude: number;
  zoom: number;
};

// Component interface
interface IProps {
  listing: ListingDetails_ListingData_listing;
}

// Form data validation schema
const validationSchema = yup.object().shape({
  prices: yup.array().of(
    yup.object().shape({
      amount: yup.number().max(999999999).nullable(true),
    })
  ),
  bedrooms: yup.number().min(0).max(99).nullable(true),
  bathrooms: yup.number().max(99).nullable(true),
  dimensions: yup.string().max(32).nullable(true),
  year: yup.number().max(9999).nullable(true),
  description: yup.string(),
});

const Details: React.FC<IProps> = ({ listing }) => {
  /**
   * States
   */
  const [loading, setLoading] = useState<boolean>(false);
  const [viewport, setViewport] = useState<Viewport>({
    width: '100%',
    height: '100%',
    latitude: _.get(listing, 'property.address.lat', 45.421532),
    longitude: _.get(listing, 'property.address.lng', -75.697189),
    zoom: 15,
  });

  /**
   * Hooks
   */

  // Notification
  const notifier = useNotifier();

  // react-router-dom usehistory hook
  let history = useHistory();

  // Form Management
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset: resetForm,
  } = useForm<ListingDetails_ListingData_listing_property>({
    // resolver: yupResolver(validationSchema),
    // mode: 'onBlur',
    // reValidateMode: 'onChange',
  });

  // Update User Graph Mutation
  const [updateListing] = useMutation(LISTING_UPDATE, {
    onCompleted: () => {
      setLoading(false);
      notifier.showMessage(
        'Successfully update!',
        'Updated Property details have been saved.',
        'Success'
      );
    },
  });

  /**
   * Handlers
   */

  // On form submit action
  const onSubmit = (data: ListingDetails_ListingData_listing_property) => {
    setLoading(true);

    const sanitizedValues = pickBy(data, (value) => {
      if (isArray(value)) {
        return (value?.[0] as any)?.amount;
      }
      return value;
    });

    if (data.prices?.[0]?.amount) {
      data.prices[0].amount = parseInt(data.prices[0].amount as any);
    }

    if (data.year) {
      data.year = parseInt(data.year as any);
    }

    updateListing({
      variables: {
        listingId: listing.id,
        listing: { property: { ...sanitizedValues } },
      },
    });
    resetForm({ ...data });
  };

  return (
    <div>
      {/* Listing Details map */}
      <div className="w-full h-48 rounded-md overflow-hidden">
        <ReactMapGL
          {...viewport}
          mapboxApiAccessToken={config.map.token}
          onViewportChange={(nextViewport: Viewport) =>
            setViewport(nextViewport)
          }
          mapStyle="mapbox://styles/mkgo/ckmjw0nio14wn17o4pa8uqase"
        >
          {_.get(listing, 'property.address.lat', false) &&
            _.get(listing, 'property.address.lng', false) && (
              <Marker
                offsetTop={-32}
                offsetLeft={-16}
                latitude={listing.property!.address!.lat!}
                longitude={listing.property!.address!.lng!}
              >
                <img className="w-8 h-8" src={MarkerIcon} alt="Listing" />
              </Marker>
            )}
        </ReactMapGL>
      </div>
      <div>
        <div className="mt-5 md:mt-5 md:col-span-2">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Listing Details section */}
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <InputGroup label="Price">
                  <InputField
                    type="number"
                    {...register('prices.0.amount')}
                    defaultValue={_.get(
                      listing,
                      'property.prices[0].amount',
                      null
                    )}
                    error={errors.prices && (errors.prices as any)[0]?.amount}
                  />
                </InputGroup>
              </div>
              <div className="flex flex-row space-x-6 items-center">
                <div className="flex space-x-2 items-center">
                  <input
                    type="checkbox"
                    {...register('hasGarage')}
                    defaultChecked={_.get(listing, 'property.hasGarage', false)}
                    className="focus:ring-primary-light h-4 w-4 text-primary-dark border-gray-300 rounded shadow-sm"
                  />
                  <label htmlFor="garage" className="font-medium text-gray-700">
                    Garage
                  </label>
                </div>
                <div className="flex space-x-2 items-center">
                  <input
                    type="checkbox"
                    {...register('hasFireplace')}
                    defaultChecked={_.get(
                      listing,
                      'property.hasFireplace',
                      false
                    )}
                    className="focus:ring-primary-light h-4 w-4 text-primary-dark border-gray-300 rounded shadow-sm"
                  />
                  <label
                    htmlFor="fireplace"
                    className="font-medium text-gray-700"
                  >
                    Fireplace
                  </label>
                </div>
                <div className="flex space-x-2 items-center">
                  <input
                    type="checkbox"
                    {...register('hasPool')}
                    defaultChecked={_.get(listing, 'property.hasPool', false)}
                    className="focus:ring-primary-light h-4 w-4 text-primary-dark border-gray-300 rounded shadow-sm"
                  />
                  <label htmlFor="pool" className="font-medium text-gray-700">
                    Pool
                  </label>
                </div>
                <div className="flex space-x-2 items-center">
                  <input
                    type="checkbox"
                    {...register('hasAC')}
                    defaultChecked={_.get(listing, 'property.hasAC', false)}
                    className="focus:ring-primary-light h-4 w-4 text-primary-dark border-gray-300 rounded shadow-sm"
                  />
                  <label htmlFor="ac" className="font-medium text-gray-700">
                    AC
                  </label>
                </div>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <InputGroup label="Bedrooms">
                  <InputField
                    type="text"
                    {...register('bedrooms')}
                    defaultValue={_.get(listing, 'property.bedrooms', null)}
                    error={errors.bedrooms as any}
                  />
                </InputGroup>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <InputGroup label="Bathrooms">
                  <InputField
                    type="text"
                    {...register('bathrooms')}
                    defaultValue={_.get(listing, 'property.bathrooms', null)}
                    error={errors.bathrooms as any}
                  />
                </InputGroup>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <InputGroup label="Dimension">
                  <InputField
                    type="text"
                    {...register('dimensions')}
                    defaultValue={_.get(listing, 'property.dimensions', '')}
                    error={errors.dimensions as any}
                  />
                </InputGroup>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <InputGroup label="Year Built">
                  <InputField
                    type="number"
                    {...register('year')}
                    defaultValue={_.get(listing, 'property.year', null)}
                    error={errors.year as any}
                  />
                </InputGroup>
              </div>
              <div className="col-span-6">
                <InputGroup label="Description">
                  <TextAreaField
                    type="text"
                    {...register('description')}
                    defaultValue={_.get(listing, 'property.description', '')}
                    rows={7}
                    error={errors.description as any}
                  />
                </InputGroup>
              </div>
            </div>
            <div className="pt-8">
              <div className="flex justify-end">
                <CancelFormButton
                  hasChanges={isDirty}
                  onClick={isDirty ? () => history.goBack() : () => {}}
                  onCleanUp={() => resetForm()}
                  disabled={!isDirty}
                />
                <SaveFormButton loading={loading} disabled={!isDirty} />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Details;

const LISTING_UPDATE = gql`
  mutation ListingDetails_UpdateData(
    $listingId: String!
    $listing: ListingInput!
  ) {
    listingUpdate(listingId: $listingId, listing: $listing) {
      id
      sid
      property {
        id
        address {
          street
          city
        }
        prices {
          amount
          setAt
        }
        bedrooms
        bathrooms
        dimensions
        year
        description
        hasGarage
        hasFireplace
        hasPool
        hasAC
      }
    }
  }
`;

//////
// Abstraction of components created during Profile JSX cleanup
//

/**
 * TODO: Create/Move to /components/InputGroup.tsx and import
 */
const InputGroup = ({
  label,
  children,
  ...props
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label {...props} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
  </div>
);
/**
 * TODO: Create/Move to /components/InputField.tsx and import
 */
const InputField = React.forwardRef<
  HTMLInputElement,
  {
    error?: any;
    [x: string]: any;
  }
>(({ error, ...props }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      {...props}
      className={`${
        error
          ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
          : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
      } appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm`}
    />
    {error ? (
      <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
      </div>
    ) : null}
    <p className="mt-2 text-sm text-red-600">{error?.message}</p>
  </div>
));

/**
 * TODO: Create/Move to /components/TextAreaField.tsx and import
 */
const TextAreaField = React.forwardRef<
  HTMLTextAreaElement,
  {
    error?: any;
    [x: string]: any;
  }
>(({ error, ...props }, ref) => (
  <div className="relative">
    <textarea
      ref={ref}
      {...props}
      className={`${
        error
          ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
          : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
      } appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm`}
    />
    {error ? (
      <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
      </div>
    ) : null}
    <p className="mt-2 text-sm text-red-600">{error?.message}</p>
  </div>
));
